import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFormErrors } from "../../../../@vodea/utilities/hooks";
import Form from "antd/lib/form";
import { Helmet } from "react-helmet-async";
import VuiContainer from "../../../../@vodea/vodea-ui/components/Container";
import { Card, Col, DatePicker, PageHeader, Row, Select, Space } from "antd";
import {
  handleBackendError,
  openNotification,
} from "../../../../functions/global";
import FormAction from "../../../../components/FormAction";
import { ACCESS_PERMISSION } from "../../../../constant";
import { COST_INFORMATION_DURATION_OPTIONS } from "../../../../constant";
import {
  scheduleSessionTypes,
  scheduleTypes,
} from "../../../../constant/schedule";
import VuiSelectSingle from "../../../../@vodea/vodea-ui/components/Select/Single";
import _ from "lodash";
import PsychologistRepository from "../../../../repositories/PsychologistRepository";
import { useState } from "@hookstate/core";
import React, { useState as useReactState } from "react";
import CategoryRepository from "../../../../repositories/CategoryRepository";
import CustomerRepository from "../../../../repositories/CustomerRepository";
import PaymentMethodRepository from "../../../../repositories/PaymentMethodRepository";
import AssessmentRepository from "../../../../repositories/AssessmentRepository";
import ScheduleRepository from "../../../../repositories/ScheduleRepository";
import ScheduleTimeRepository from "../../../../repositories/ScheduleTimeRepository";
import {
  Schedule,
  ScheduleSessionType,
  ScheduleTimeOptions,
  ScheduleType,
} from "../../../../entities/schedule";
import { BookingFormData } from "./interface";
import { useCallback, useEffect, useMemo } from "react";
import { AxiosError, AxiosResponse } from "axios";
import moment, { Moment } from "moment";
import BookingRepository from "../../../../repositories/BookingRepository";
import { BookingCreatePayload } from "../../../../entities/dtos/booking";
import { AssessmentSchedule } from "../../../../entities/assessment";
const { Option } = Select;

let title = "Booking";

const AppBookingForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pageTitle = t("common.text.addNewItem", { item: title });
  const [form] = Form.useForm();
  const { clearFormError } = useFormErrors();

  const disable = useState(false);
  const loading = useState(false);
  const calendarLoading = useState(false);
  const singleSessionTimes = useState<ScheduleTimeOptions[]>([]);
  const multiSessionTimes = useState<ScheduleTimeOptions[][]>([]);
  const calendarData = useState<Schedule[]>([]);
  const [selectedScheduleId, setSelectedScheduleId] = useReactState<
    number | undefined
  >(undefined);

  const sessionType = Form.useWatch(
    "session_type",
    form,
  ) as ScheduleSessionType;
  const counselingMethod = Form.useWatch(
    "counseling_method",
    form,
  ) as ScheduleType;
  const categoryId = Form.useWatch("category_id", form);
  const assessmentId = Form.useWatch("assessment_id", form);
  const psychologistId = Form.useWatch("psychologist_id", form);
  const totalSessions = Form.useWatch("total_sessions", form);
  const [selectedAssessment, setSelectedAssessment] = useReactState<{
    number_of_sessions?: number | undefined;
    sessions: AssessmentSchedule[];
    hasSchedule: boolean;
    durations?: number[] | undefined;
  }>({ sessions: [], hasSchedule: false });

  useEffect(() => {
    if (sessionType === "ASSESSMENT" && assessmentId) {
      const id = assessmentId?.value || assessmentId;
      if (!id) return;

      AssessmentRepository.show(id, { with: ["sessions"] })
        .then((res: AxiosResponse) => {
          const data = res?.data?.data;

          if (data && Array.isArray(data.sessions)) {
            const sessions = data.sessions;
            const durations = sessions.map(
              (s: { session_duration: number }) => s.session_duration,
            );

            setSelectedAssessment({
              number_of_sessions: data.number_of_sessions,
              sessions,
              hasSchedule: data.has_schedule,
              durations,
            });

            form.setFieldsValue({
              total_sessions: data.number_of_sessions,
            });
          }
        })
        .catch(() => {
          setSelectedAssessment({
            sessions: [],
            hasSchedule: false,
            number_of_sessions: undefined,
            durations: undefined,
          });
          form.setFieldsValue({ total_sessions: undefined });
        });
    } else {
      setSelectedAssessment({
        sessions: [],
        hasSchedule: false,
        number_of_sessions: undefined,
        durations: undefined,
      });
    }
  }, [sessionType, assessmentId, form]);

  const isCounseling = sessionType === "COUNSELING";
  const isMultiSession =
    (sessionType === "COUNSELING" && totalSessions > 1) ||
    (sessionType === "ASSESSMENT" &&
      (selectedAssessment.number_of_sessions ?? 0) > 1);

  const loadCalendarData = useCallback(
    async (
      psychologistId: number,
      counselingMethod: ScheduleType,
      sessionType: ScheduleSessionType,
    ) => {
      if (sessionType && psychologistId && counselingMethod) {
        calendarLoading.set(true);
        await ScheduleRepository.findAll({
          psychologist: psychologistId,
          type: counselingMethod,
          session_type: sessionType,
        })
          .then((response: AxiosResponse) => {
            const { data: responseData } = response.data;
            calendarData.set(responseData);
            form.setFieldsValue({
              date: undefined,
              duration: undefined,
              time: undefined,
              sessions: Array.isArray(form.getFieldValue("sessions"))
                ? form.getFieldValue("sessions").map(() => ({
                    date: undefined,
                    duration: undefined,
                    time: undefined,
                  }))
                : undefined,
            });
            singleSessionTimes.set([]);
            multiSessionTimes.set([]);
          })
          .catch((error: AxiosError) => {
            handleBackendError(error);
          })
          .finally(() => {
            calendarLoading.set(false);
          });
      } else {
        calendarData.set([]);
      }
    },
    [
      calendarLoading,
      calendarData,
      form,
      singleSessionTimes,
      multiSessionTimes,
    ],
  );

  const getDisabledDate = useCallback(
    (currentDate: Moment) => {
      const availableDates = calendarData
        .get()
        .map((item) => item.date)
        .includes(currentDate.format("YYYY-MM-DD"));
      return !availableDates;
    },
    [calendarData],
  );

  const getSelectedDatesFromOtherSessions = useCallback(
    (currentSessionIndex: number) => {
      if (!isMultiSession) return [];
      return (form.getFieldValue("sessions") || [])
        .filter((_: any, idx: number) => idx !== currentSessionIndex)
        .map((session: any) => {
          const date = moment(session?.date);
          return date.isValid() ? date.format("YYYY-MM-DD") : null;
        })
        .filter(Boolean);
    },
    [form, isMultiSession],
  );

  const getDisabledDateForSession = useCallback(
    (currentDate: Moment, sessionIndex?: number) => {
      const currentDateString = currentDate.format("YYYY-MM-DD");
      const availableDates = calendarData
        .get()
        .map((item) => item.date)
        .includes(currentDateString);

      if (!availableDates) return true;

      if (isMultiSession && sessionIndex !== undefined) {
        const selectedDatesFromOtherSessions =
          getSelectedDatesFromOtherSessions(sessionIndex);
        if (selectedDatesFromOtherSessions.includes(currentDateString)) {
          return true;
        }
      }

      if (!isCounseling && isMultiSession && sessionIndex !== undefined) {
        if (currentDate.isBefore(moment(), "day")) {
          return true;
        }

        const lastSessionIndex = sessionIndex - 1;
        const minDaysBetweenSessions =
          selectedAssessment.sessions?.[lastSessionIndex]?.session_min_days ??
          0;
        const selectedDatesFromOtherSessions =
          getSelectedDatesFromOtherSessions(sessionIndex).map(
            (dateStr: string) => moment(dateStr, "YYYY-MM-DD"),
          );
        for (const otherDate of selectedDatesFromOtherSessions) {
          if (
            otherDate.isValid() &&
            Math.abs(currentDate.diff(otherDate, "days")) <
              minDaysBetweenSessions
          ) {
            return true;
          }
        }
      }

      return false;
    },
    [
      calendarData,
      isMultiSession,
      getSelectedDatesFromOtherSessions,
      isCounseling,
      selectedAssessment,
    ],
  );

  const filterTimesByDuration = (
    times: ScheduleTimeOptions[] = [],
    duration?: string,
  ): { label: string; value: string }[] => {
    let filtered: ScheduleTimeOptions[] = [];

    if (times && duration) {
      if (duration === "90") {
        filtered = times.filter(
          (time) => time.is_available_in_ninety_minutes === true,
        );
      } else if (duration === "60") {
        filtered = times.filter(
          (time) => time.is_available_in_sixty_minutes === true,
        );
      } else {
        filtered = times;
      }
    } else {
      filtered = times || [];
    }

    return filtered.map((time) => ({
      label: time.label,
      value: time.timestamp,
    }));
  };

  const currentSingleSessionTimes = singleSessionTimes.get();
  const singleSessionTimeOptions = useMemo(() => {
    const duration = form.getFieldValue("duration");
    return filterTimesByDuration(currentSingleSessionTimes, duration);
  }, [currentSingleSessionTimes, form]);

  const handleSelectDate = useCallback(
    async (date: Moment, sessionIndex?: number) => {
      const formattedDate = date.format("YYYY-MM-DD");
      const findDate = calendarData
        .get()
        .find((item) => item.date === formattedDate);
      if (!findDate) return;

      let selectedDateTimes = (findDate.times ?? []) as ScheduleTimeOptions[];

      if (sessionType === "ASSESSMENT") {
        try {
          calendarLoading.set(true);
          const duration = selectedAssessment.durations?.[sessionIndex ?? 0];
          const response = await ScheduleTimeRepository.findAll({
            psychologist_id: psychologistId?.value,
            session_type: sessionType,
            type: counselingMethod,
            date: formattedDate,
            duration,
          });
          selectedDateTimes = (response.data.data ?? []).filter(
            (t: ScheduleTimeOptions) => t.is_available,
          );
        } catch (error) {
          handleBackendError(error as AxiosError);
        } finally {
          calendarLoading.set(false);
        }
      } else {
        const duration =
          isMultiSession && sessionIndex !== undefined
            ? form.getFieldValue(["sessions", sessionIndex, "duration"])
            : form.getFieldValue("duration");

        if (isMultiSession) {
          selectedDateTimes = selectedDateTimes.filter(
            (t) => t.is_available_in_sixty_minutes,
          );
        } else if (duration === "90") {
          selectedDateTimes = selectedDateTimes.filter(
            (t) => t.is_available_in_ninety_minutes,
          );
        } else if (duration === "60") {
          selectedDateTimes = selectedDateTimes.filter(
            (t) => t.is_available_in_sixty_minutes,
          );
        }
      }

      const times: ScheduleTimeOptions[] = selectedDateTimes.map((time) => {
        if (sessionType === "ASSESSMENT") {
          return { ...time, label: moment(time.timestamp).format("HH:mm") };
        } else {
          const durationValue =
            isMultiSession && sessionIndex !== undefined
              ? form.getFieldValue(["sessions", sessionIndex, "duration"])
              : form.getFieldValue("duration");

          const durationOption = COST_INFORMATION_DURATION_OPTIONS.find(
            (option) => option.value.toString() === durationValue,
          );
          const durationMinutes = durationOption ? durationOption.value : 60;

          return {
            ...time,
            label: `${moment(time.timestamp).format("HH:mm")} - ${moment(
              time.timestamp,
            )
              .add(durationMinutes, "minutes")
              .format("HH:mm")}`,
          };
        }
      });

      if (
        (isMultiSession || sessionType === "ASSESSMENT") &&
        sessionIndex !== undefined
      ) {
        multiSessionTimes.set((prev) => {
          const arr = Array.isArray(prev) ? [...prev] : [];
          arr[sessionIndex] = times;
          return arr;
        });

        const currentSessions = form.getFieldValue("sessions") || [];
        const updatedSessions = currentSessions.map((s: any, idx: number) =>
          idx === sessionIndex
            ? {
                ...s,
                time: undefined,
                times,
                schedule_id: findDate.id || s?.schedule_id || null,
              }
            : s,
        );

        form.setFieldsValue({ sessions: updatedSessions });
      } else {
        form.setFieldsValue({ time: undefined, times });
        setSelectedScheduleId(findDate.id);
        singleSessionTimes.set(times);
      }
    },
    [
      calendarData,
      form,
      isMultiSession,
      singleSessionTimes,
      multiSessionTimes,
      setSelectedScheduleId,
      sessionType,
      psychologistId,
      counselingMethod,
      selectedAssessment.durations,
      calendarLoading,
    ],
  );

  useEffect(() => {
    const psychologistIdValue = psychologistId?.value;
    if (
      psychologistIdValue &&
      counselingMethod &&
      ["ASSESSMENT", "COUNSELING"].includes(sessionType) &&
      (sessionType !== "ASSESSMENT" || assessmentId)
    ) {
      loadCalendarData(psychologistIdValue, counselingMethod, sessionType);
    } else {
      calendarData.set([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologistId, counselingMethod, sessionType, assessmentId]);

  const onFinish = async (data: BookingFormData) => {
    loading.set(true);

    const {
      customer_id,
      psychologist_id,
      category_id,
      duration,
      payment_method_id,
      assessment_id,
      sessions,
    } = data ?? {};

    const getId = (field: any) => field?.value ?? field;

    let scheduleIds: number[] = [];
    if (Array.isArray(sessions) && sessions.length > 0) {
      scheduleIds = sessions
        .map((session: any) => {
          if (session && session.time) {
            const formattedDate = moment(session.date).format("YYYY-MM-DD");
            const found = calendarData
              .get()
              .find(
                (item) =>
                  item.date === formattedDate ||
                  item.id === session.schedule_id,
              );
            if (!found) return undefined;

            const timeObj = Array.isArray(found.times)
              ? found.times.find(
                  (t: any) =>
                    t.timestamp === session.time ||
                    t.timestamp === String(session.time),
                )
              : undefined;
            const id = timeObj?.id ?? found.id;
            return typeof id === "number" ? id : undefined;
          }
          return undefined;
        })
        .filter((id): id is number => typeof id === "number");
    } else if (typeof selectedScheduleId === "number") {
      const selectedDate = form.getFieldValue("date");
      const selectedTime = form.getFieldValue("time");
      if (selectedDate && selectedTime) {
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        const found = calendarData
          .get()
          .find(
            (item) =>
              item.date === formattedDate || item.id === selectedScheduleId,
          );
        const timeObj = found?.times?.find(
          (t: any) =>
            t.timestamp === selectedTime ||
            t.timestamp === String(selectedTime),
        );
        const id = timeObj?.id ?? selectedScheduleId;
        scheduleIds = typeof id === "number" ? [id] : [selectedScheduleId];
      } else {
        scheduleIds = [selectedScheduleId];
      }
    }

    const payload: BookingCreatePayload = {
      customer_id: getId(customer_id),
      psychologist_id: getId(psychologist_id),
      schedule_ids: scheduleIds,
      assessment_id: getId(assessment_id),
      category_id: getId(category_id),
      duration,
      payment_method_id: getId(payment_method_id),
      session_amount: totalSessions,
    };

    await BookingRepository.create(payload)
      .then(() => {
        navigate(-1);
        openNotification(
          "success",
          t("notification.success.createItem", { item: title }),
        );
      })
      .catch((e: AxiosError) => {
        handleBackendError(e);
      })
      .finally(() => {
        loading.set(false);
      });
  };

  const FormItemWrapper: React.FC<{ children: React.ReactNode }> = ({
    children,
  }) => (
    <div className="vui-form-group type-column">
      <div className="input-section">{children}</div>
    </div>
  );

  const renderLeftSection = () => (
    <Col xs={24} lg={8}>
      <Card>
        <Space style={{ width: "100%" }} direction="vertical" size={0}>
          <FormItemWrapper>
            {isCounseling ? (
              <Form.Item
                name="category_id"
                label="Category"
                rules={[
                  {
                    required: true,
                    message: t("validation.required", { item: "Category" }),
                  },
                ]}
              >
                <VuiSelectSingle
                  repository={CategoryRepository}
                  onChange={() => {
                    form.resetFields(["psychologist_id"]);
                  }}
                  disabled={disable.get()}
                  placeholder={t("select.placeholder", { item: "Category" })}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name="assessment_id"
                label="Package"
                rules={[
                  {
                    required: true,
                    message: t("validation.required", { item: "Package" }),
                  },
                ]}
              >
                <VuiSelectSingle
                  repository={AssessmentRepository}
                  onChange={() => {
                    form.resetFields(["psychologist_id"]);
                  }}
                  disabled={disable.get()}
                  placeholder={t("select.placeholder", { item: "Package" })}
                />
              </Form.Item>
            )}
          </FormItemWrapper>

          {(isCounseling || selectedAssessment.hasSchedule) && (
            <>
              <FormItemWrapper>
                <Form.Item
                  name="psychologist_id"
                  label="Psychologist"
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: "Psychologist",
                      }),
                    },
                  ]}
                >
                  <VuiSelectSingle
                    repository={PsychologistRepository}
                    selectParams={{
                      category_id: categoryId?.value,
                      assessment_id: assessmentId?.value,
                    }}
                    disabled={disable.get()}
                    placeholder={t("select.placeholder", {
                      item: "Psychologist",
                    })}
                  />
                </Form.Item>
              </FormItemWrapper>

              <FormItemWrapper>
                <Form.Item
                  name="counseling_method"
                  label="Counseling Method"
                  rules={[
                    {
                      required: true,
                      message: t("validation.required", {
                        item: "Counseling Method",
                      }),
                    },
                  ]}
                >
                  <Select
                    style={{ width: "100%" }}
                    options={scheduleTypes.map((item) => ({
                      label: _.capitalize(item),
                      value: item,
                    }))}
                    disabled={disable.get()}
                    placeholder={t("select.placeholder", {
                      item: "Counseling Method",
                    })}
                  />
                </Form.Item>
              </FormItemWrapper>
            </>
          )}

          {isCounseling && (
            <FormItemWrapper>
              <Form.Item
                name="total_sessions"
                label="Total Counseling"
                rules={[
                  {
                    required: true,
                    message: t("validation.required", {
                      item: "Total Sessions",
                    }),
                  },
                ]}
              >
                <Select
                  style={{ width: "100%" }}
                  options={[
                    { label: "Single Session", value: 1 },
                    { label: "5 Sessions", value: 5 },
                  ]}
                  disabled={disable.get()}
                  placeholder={t("select.placeholder", {
                    item: "Total Sessions",
                  })}
                  size="large"
                />
              </Form.Item>
            </FormItemWrapper>
          )}
        </Space>
      </Card>
    </Col>
  );

  const renderRightSection = () => (
    <Col xs={24} lg={16}>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          {isMultiSession || sessionType === "ASSESSMENT" ? (
            <>
              {Array.from({
                length:
                  sessionType === "ASSESSMENT"
                    ? selectedAssessment.number_of_sessions || 0
                    : totalSessions,
              }).map((_, idx) => (
                <Card key={idx} title={`Session ${idx + 1}`}>
                  <Row gutter={[20, 20]}>
                    <Col xs={24} lg={12}>
                      <Form.Item
                        name={["sessions", idx, "date"]}
                        label="Date"
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", { item: "Date" }),
                          },
                        ]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          disabled={disable.get()}
                          placeholder={t("select.placeholder", {
                            item: "Date",
                          })}
                          size="large"
                          onSelect={(date) => {
                            if (date) handleSelectDate(date, idx);
                          }}
                          disabledDate={(currentDate) =>
                            getDisabledDateForSession(currentDate, idx)
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Form.Item noStyle shouldUpdate>
                        {() => (
                          <Form.Item
                            name={["sessions", idx, "time"]}
                            label="Time"
                            rules={[
                              {
                                required: true,
                                message: t("validation.required", {
                                  item: "Time",
                                }),
                              },
                            ]}
                          >
                            <Select
                              style={{ width: "100%" }}
                              options={multiSessionTimes
                                .get()
                                ?.[idx]?.map((time) => ({
                                  label: moment(time.timestamp).format("HH:mm"),
                                  value: time.timestamp,
                                }))}
                              disabled={
                                disable.get() ||
                                !form.getFieldValue(["sessions", idx, "date"])
                              }
                              placeholder={t("select.placeholder", {
                                item: "Time",
                              })}
                              size="large"
                              allowClear
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
            </>
          ) : (
            <Card title="Session">
              <Row gutter={[20, 20]}>
                <Col xs={24} lg={8}>
                  <Form.Item
                    name="date"
                    label="Date"
                    rules={[
                      {
                        required: true,
                        message: t("validation.required", { item: "Date" }),
                      },
                    ]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabled={disable.get()}
                      placeholder={t("select.placeholder", { item: "Date" })}
                      size="large"
                      onSelect={(date) => handleSelectDate(date)}
                      onChange={() => {
                        form.setFieldsValue({
                          duration: undefined,
                          time: undefined,
                        });
                      }}
                      disabledDate={getDisabledDate}
                    />
                  </Form.Item>
                </Col>
                {sessionType === "COUNSELING" && !isMultiSession && (
                  <Col xs={24} lg={8}>
                    <Form.Item
                      name="duration"
                      label="Duration"
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: "Duration",
                          }),
                        },
                      ]}
                    >
                      <Select
                        style={{ width: "100%" }}
                        options={COST_INFORMATION_DURATION_OPTIONS.map(
                          (option) => ({
                            label: option.label,
                            value: option.value.toString(),
                          }),
                        )}
                        disabled={disable.get() || !form.getFieldValue("date")}
                        placeholder={t("select.placeholder", {
                          item: "Duration",
                        })}
                        size="large"
                        allowClear
                        onChange={() => {
                          const selectedDate = form.getFieldValue("date");
                          if (selectedDate) handleSelectDate(selectedDate);
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
                <Col xs={24} lg={8}>
                  <Form.Item
                    name="time"
                    label="Time"
                    rules={[
                      {
                        required: true,
                        message: t("validation.required", { item: "Time" }),
                      },
                    ]}
                  >
                    <Select
                      style={{ width: "100%" }}
                      options={singleSessionTimeOptions}
                      disabled={
                        disable.get() ||
                        (sessionType === "COUNSELING" &&
                          !form.getFieldValue("duration"))
                      }
                      placeholder={t("select.placeholder", { item: "Time" })}
                      size="large"
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}
        </Space>
      </Card>
    </Col>
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <VuiContainer bottomSpace={110}>
        <PageHeader onBack={() => navigate(-1)} title={pageTitle} />
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={() => {
            openNotification("warning", t("notification.error.uncompleteForm"));
          }}
          onValuesChange={clearFormError}
        >
          <Row gutter={[20, 20]}>
            <Col xs={24}>
              <Card>
                <Row gutter={[20, 20]}>
                  <Col xs={24} lg={6}>
                    <Form.Item
                      name="session_type"
                      label={"Booking Type"}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: "Booking Type",
                          }),
                        },
                      ]}
                    >
                      <Select
                        style={{ width: "100%" }}
                        options={scheduleSessionTypes.map((item) => ({
                          label: _.capitalize(item),
                          value: item,
                        }))}
                        placeholder={t("select.placeholder", {
                          item: "Booking Type",
                        })}
                        size="large"
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item
                      name="payment_method_id"
                      label={"Payment Method"}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: "Payment Method",
                          }),
                        },
                      ]}
                    >
                      <VuiSelectSingle
                        repository={PaymentMethodRepository}
                        selectParams={{ is_admin: true }}
                        placeholder={t("select.placeholder", {
                          item: "Payment Method",
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name="customer_id"
                      label={"Patient Name"}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: "Patient Name",
                          }),
                        },
                      ]}
                    >
                      <VuiSelectSingle
                        repository={CustomerRepository}
                        selectParams={{
                          is_backoffice: true,
                          is_admin: true,
                          per_page: 100,
                        }}
                        placeholder={t("select.placeholder", {
                          item: "Patient Name",
                        })}
                        customOptions={(item: any) => {
                          return (
                            <Option
                              key={`${item.id}-${item.name}`}
                              value={item.id}
                              label={item?.name}
                            >
                              {item?.name}
                              {item?.email || item?.phone ? " ( " : ""}
                              {item?.email ? `${item.email}` : ""}
                              {item?.phone_number
                                ? ` ${item.email ? " - " : ""} ${item?.phone_country}${item.phone_number}`
                                : ""}
                              {item?.email || item?.phone_number ? " ) " : ""}
                            </Option>
                          );
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
            {sessionType && renderLeftSection()}
            {((sessionType === "ASSESSMENT" &&
              selectedAssessment.number_of_sessions) ||
              sessionType === "COUNSELING") &&
              renderRightSection()}
            <Col span={24}>
              <FormAction
                formName={title}
                cancelTitle={t("common.button.cancel")}
                onCancel={() => navigate(-1)}
                onSubmitting={loading.get()}
                submitAccess={ACCESS_PERMISSION.booking.store}
              />
            </Col>
          </Row>
        </Form>
      </VuiContainer>
    </>
  );
};

export default AppBookingForm;
